/**
 * BoardListPage — composed page that wraps the isolated BoardList component
 * with BoardsProvider state, star/unstar actions, loading/error states,
 * and cursor-based infinite scroll pagination.
 *
 * Requirements: 6.1, 6.2, 6.3, 6.4, 6.5, 6.6
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BoardsProvider, useBoards } from '../state/boards-context';
import { BoardList } from './BoardList';
import { InlineCreationForm } from '../components/InlineCreationForm';
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { MyWorkSummary } from '../components/dashboard/MyWorkSummary';
import { PublicBoardList } from '../components/PublicBoardList';

/**
 * Inner component that consumes BoardsContext.
 * Separated from the provider wrapper so the hook has access to context.
 */
function BoardListContent(): React.ReactElement {
  const navigate = useNavigate();
  const { state, fetchMore, starBoard, unstarBoard, createBoard, refetch } = useBoards();
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Inline board creation state
  const [creatingBoard, setCreatingBoard] = useState(false);
  const [boardColor, setBoardColor] = useState('#0079bf');
  const [boardCreateError, setBoardCreateError] = useState<string | null>(null);

  // IntersectionObserver for infinite scroll pagination
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && state.hasMore && !state.isLoading) {
          fetchMore();
        }
      },
      { rootMargin: '200px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [state.hasMore, state.isLoading, fetchMore]);

  const handleBoardClick = useCallback(
    (boardId: string) => {
      navigate(`/boards/${boardId}`);
    },
    [navigate],
  );

  const handleStarToggle = useCallback(
    (boardId: string, currentlyStarred: boolean) => {
      if (currentlyStarred) {
        unstarBoard(boardId);
      } else {
        starBoard(boardId);
      }
    },
    [starBoard, unstarBoard],
  );

  const handleCreateBoardClick = useCallback(() => {
    setCreatingBoard(true);
    setBoardCreateError(null);
  }, []);

  const handleCreateBoardSubmit = useCallback(async (title: string, color?: string): Promise<void> => {
    setBoardCreateError(null);
    try {
      await createBoard(title, color ?? boardColor);
      setCreatingBoard(false);
      setBoardColor('#0079bf');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create board';
      setBoardCreateError(message);
      throw error;
    }
  }, [createBoard]);

  const handleCreateBoardCancel = useCallback(() => {
    setCreatingBoard(false);
    setBoardCreateError(null);
    setBoardColor('#0079bf');
  }, []);

  // Loading state — initial load with no boards yet
  if (state.isLoading && state.boards.length === 0) {
    return (
      <main className="min-h-screen bg-background p-6" role="main" aria-busy="true">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-center justify-center py-12" role="status" aria-label="Loading boards">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" aria-hidden="true" />
            <span className="ml-3 text-muted-foreground">Loading boards…</span>
          </div>
        </div>
      </main>
    );
  }

  // Error state
  if (state.error && state.boards.length === 0) {
    return (
      <main className="min-h-screen bg-background p-6" role="main">
        <div className="mx-auto max-w-7xl">
          <Alert variant="destructive">
            <AlertTitle>Error loading boards</AlertTitle>
            <AlertDescription>{state.error}</AlertDescription>
          </Alert>
          <Button className="mt-4" onClick={refetch}>
            Retry
          </Button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background p-6" role="main">
      <div className="mx-auto max-w-7xl">
        <MyWorkSummary />

        {creatingBoard ? (
          <BoardListWithInlineForm
            boards={state.boards}
            onBoardClick={handleBoardClick}
            onStarToggle={handleStarToggle}
            onCreateBoardSubmit={handleCreateBoardSubmit}
            onCreateBoardCancel={handleCreateBoardCancel}
            boardColor={boardColor}
            onColorSelect={setBoardColor}
            boardCreateError={boardCreateError}
          />
        ) : (
          <BoardList
            boards={state.boards}
            onBoardClick={handleBoardClick}
            onCreateBoard={handleCreateBoardClick}
            onStarToggle={handleStarToggle}
          />
        )}

        {/* Inline error when pagination fails but boards are already showing */}
        {state.error && state.boards.length > 0 && (
          <div className="mx-6 mb-4">
            <Alert variant="warning">
              <AlertDescription>
                {state.error}{' '}
                <button
                  type="button"
                  className="underline font-medium hover:no-underline"
                  onClick={refetch}
                >
                  Retry
                </button>
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Loading indicator during pagination */}
        {state.isLoading && state.boards.length > 0 && (
          <div className="flex justify-center py-4" role="status" aria-label="Loading more boards">
            <span className="text-sm text-muted-foreground">Loading more boards…</span>
          </div>
        )}

        {/* Sentinel element for IntersectionObserver-based infinite scroll */}
        {state.hasMore && (
          <div ref={sentinelRef} aria-hidden="true" data-testid="scroll-sentinel" />
        )}

        {/* Public Boards section */}
        <section className="mt-8" aria-label="Public boards">
          <h2 className="text-lg font-semibold text-foreground mb-4">Public Boards</h2>
          <PublicBoardList />
        </section>
      </div>
    </main>
  );
}

/**
 * BoardListPage — wraps its own BoardsProvider and renders the content.
 * This is the component that gets mounted in the router.
 */
export function BoardListPage(): React.ReactElement {
  return (
    <BoardsProvider>
      <BoardListContent />
    </BoardsProvider>
  );
}

// ---------------------------------------------------------------------------
// Board list variant that shows inline creation form in place of the "Create Board" tile
// ---------------------------------------------------------------------------

interface BoardListWithInlineFormProps {
  readonly boards: { boardId: string; title: string; color: string; starred: boolean; memberCount: number; role?: string }[];
  readonly onBoardClick: (boardId: string) => void;
  readonly onStarToggle: (boardId: string, currentlyStarred: boolean) => void;
  readonly onCreateBoardSubmit: (title: string, color?: string) => Promise<void>;
  readonly onCreateBoardCancel: () => void;
  readonly boardColor: string;
  readonly onColorSelect: (color: string) => void;
  readonly boardCreateError: string | null;
}

function BoardListWithInlineForm({
  boards,
  onBoardClick,
  onStarToggle,
  onCreateBoardSubmit,
  onCreateBoardCancel,
  boardColor,
  onColorSelect,
  boardCreateError,
}: BoardListWithInlineFormProps): React.ReactElement {
  const myBoards = boards.filter((b) => !b.role || b.role === 'owner');
  const sharedBoards = boards.filter((b) => b.role && b.role !== 'owner');

  const starred = myBoards.filter((b) => b.starred);
  const unstarred = myBoards.filter((b) => !b.starred);

  return (
    <div className="space-y-6 p-6">
      <h2 className="text-2xl font-bold text-foreground">My Boards</h2>

      {starred.length > 0 && (
        <section aria-labelledby="starred-heading-inline">
          <h3 id="starred-heading-inline" className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <span className="text-yellow-400">★</span> Starred Boards
          </h3>
          <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" role="list">
            {starred.map((board) => (
              <li key={board.boardId}>
                <button
                  type="button"
                  onClick={() => onBoardClick(board.boardId)}
                  className="block w-full text-left rounded-lg border border-border p-4 min-h-[100px] font-bold text-white shadow-sm hover:brightness-90 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  style={{ backgroundColor: board.color || '#0079bf' }}
                  aria-label={`Open board: ${board.title}`}
                >
                  <span className="block text-sm leading-snug break-words">{board.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section aria-labelledby="all-heading-inline">
        <h3 id="all-heading-inline" className="text-lg font-semibold text-foreground mb-4">All Boards</h3>
        <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" role="list">
          {unstarred.map((board) => (
            <li key={board.boardId} className="relative">
              <button
                type="button"
                onClick={() => onBoardClick(board.boardId)}
                className="block w-full text-left rounded-lg border border-border p-4 min-h-[100px] font-bold text-white shadow-sm hover:brightness-90 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                style={{ backgroundColor: board.color || '#0079bf' }}
                aria-label={`Open board: ${board.title}`}
              >
                <span className="block text-sm leading-snug break-words">{board.title}</span>
                <span className="block mt-2 text-xs font-normal opacity-75">
                  {board.memberCount} member{board.memberCount !== 1 ? 's' : ''}
                </span>
              </button>
              <button
                type="button"
                onClick={() => onStarToggle(board.boardId, board.starred)}
                className="absolute top-2 right-2 p-1 rounded text-white/70 hover:text-yellow-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={board.starred ? `Unstar board: ${board.title}` : `Star board: ${board.title}`}
                aria-pressed={board.starred}
              >
                <span>{board.starred ? '★' : '☆'}</span>
              </button>
            </li>
          ))}

          {/* Inline creation form in place of the "Create Board" tile */}
          <li>
            <div
              className="rounded-lg border border-border p-4 min-h-[100px] shadow-sm"
              style={{ backgroundColor: boardColor }}
            >
              <InlineCreationForm
                placeholder="Enter board title…"
                submitLabel="Create Board"
                onSubmit={onCreateBoardSubmit}
                onCancel={onCreateBoardCancel}
                error={boardCreateError}
                showColorPicker={true}
                onColorSelect={onColorSelect}
                selectedColor={boardColor}
              />
            </div>
          </li>
        </ul>
      </section>

      {sharedBoards.length > 0 && (
        <section aria-labelledby="shared-heading-inline">
          <h3 id="shared-heading-inline" className="text-lg font-semibold text-foreground mb-4">Shared with me</h3>
          <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" role="list">
            {sharedBoards.map((board) => (
              <li key={board.boardId} className="relative">
                <button
                  type="button"
                  onClick={() => onBoardClick(board.boardId)}
                  className="block w-full text-left rounded-lg border border-border p-4 min-h-[100px] font-bold text-white shadow-sm hover:brightness-90 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  style={{ backgroundColor: board.color || '#0079bf' }}
                  aria-label={`Open board: ${board.title}`}
                >
                  <span className="block text-sm leading-snug break-words">{board.title}</span>
                  <span className="block mt-2 text-xs font-normal opacity-75">
                    {board.memberCount} member{board.memberCount !== 1 ? 's' : ''}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => onStarToggle(board.boardId, board.starred)}
                  className="absolute top-2 right-2 p-1 rounded text-white/70 hover:text-yellow-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={board.starred ? `Unstar board: ${board.title}` : `Star board: ${board.title}`}
                  aria-pressed={board.starred}
                >
                  <span>{board.starred ? '★' : '☆'}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
